import {
  DatePeriod,
  LenientDatePeriod,
  TimeOfDay,
  TimeOfDayPeriod,
} from "@greeter/date";
import { useMemo } from "react";
import {
  addSeconds,
  addMinutes,
  set,
  differenceInMinutes,
  differenceInSeconds,
} from "date-fns";
import { classNames } from "@greeter/util";

import css from "./TimePicker.module.scss";

function isEqual(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate() &&
    a.getHours() === b.getHours() &&
    a.getMinutes() === b.getMinutes()
  );
}

export type IsEnabledFn = (tod: Date) => boolean;
export type OnDateChange = (tod: Date) => void;

export type TimePickerProps = {
  /**
   * We use Dates and DatePeriod due to context clues.
   * Since we handle time of days that past midnight,
   * when doing comparisons, it's good to retain the
   * date information, so we know that we've passed midnight
   */
  range: LenientDatePeriod;

  /**
   * interval in minutes
   */
  interval: number;

  selected?: Date;
  onChange?: OnDateChange;

  /**
   * optional, might be useful to return the date from the anchor
   */
  anchorDate?: Date;

  isEnabled?: IsEnabledFn;
};
export function TimePicker(props: TimePickerProps) {
  const tods = useMemo<Array<Date>>(() => {
    const result: Array<Date> = [];
    const start = props.range.from;
    const end = props.range.to;
    const diff = differenceInMinutes(end, start);
    const interval = props.interval;

    for (let i = 0; i <= diff; i += interval) {
      result.push(addMinutes(start, i));
    }

    return result;
  }, [props.range, props.interval]);

  return (
    <div className={css.TimePicker}>
      {tods.map((tod) => {
        return (
          <div
            key={TimeOfDay.fromDate(tod).toString()}
            {...classNames(
              css.TimeOfDay,
              props.isEnabled && !props.isEnabled(tod) && css.Disabled,
              props.selected && isEqual(tod, props.selected) && css.Active
            )}
            onClick={() => {
              if (props.onChange) {
                props.onChange(tod);
              }
            }}
          >
            {TimeOfDay.fromDate(tod).toString()}
          </div>
        );
      })}
    </div>
  );
}
